// Industry landing page data — one entry per niche
// Each maps to a route: /{slug}

export const INDUSTRIES = [
  {
    slug: 'trades',
    name: 'Trades',
    label: 'Trades & Construction',
    icon: 'Hammer',
    color: 'bg-amber-500',
    offerLine: 'Get a professional website and online presence for less than £7 per day. One extra job pays for the whole year.',
    short: 'Electricians, plumbers, builders, roofers, landscapers & solar.',
    heroHeadline: 'You do brilliant work. Your competitors just look like they do.',
    heroSub: "Customers can't see your workmanship from Google — they see your website, your reviews and how fast you reply. We build growth systems for trades that win the job before you've even quoted.",
    magnet: { title: 'Free Website & Lead Generation Audit', sub: "We'll review your website, Google presence and lead handling — and show you exactly where jobs are going to competitors instead. No jargon, no obligation." },
    painPoints: [
      { title: "You can't answer the phone on a job", text: 'Every missed call is a customer ringing the next number on Google. Our AI answers, qualifies and books them in — while your hands are full.' },
      { title: 'Word of mouth has a ceiling', text: "Referrals are gold, but they don't scale. A system that ranks you locally and converts visitors gives you a second pipeline you control." },
      { title: 'Quotes go quiet', text: 'You quote, they vanish. Automated follow-ups chase every quote politely and persistently — so you win the jobs you already priced.' },
      { title: 'Evenings lost to paperwork', text: 'Invoicing chasers, review requests, booking confirmations — all automated. Your evenings go back to being yours.' },
    ],
    build: ['A website that makes you the obvious choice locally', 'Instant AI response to every enquiry — 24/7', 'Automated quote follow-up sequences', 'Review system that builds your Google reputation after every job', 'Google Business Profile that ranks in the map pack', 'WhatsApp integration — quote photos straight to your phone'],
    stats: [
      { value: 60, suffix: '%', label: 'more booked work for trade clients' },
      { value: 28, suffix: 's', label: 'average AI response time' },
      { value: 12, suffix: 'hrs', label: 'admin saved weekly' },
    ],
    testimonial: {
      quote: "We used to lose jobs because I couldn't answer the phone on site. Now the AI replies before I've even seen the message. Booked work is up 60% and I've not changed anything else.",
      name: 'Dale Hartley',
      role: 'Hartley Electrical Services',
    },
  },
  {
    slug: 'care',
    name: 'Care',
    label: 'Care Providers',
    icon: 'HeartHandshake',
    color: 'bg-rose-500',
    offerLine: 'Build trust online and generate more enquiries — for less than £7 per day. One placement covers the entire year.',
    short: 'Home care, residential care & supported living providers.',
    heroHeadline: 'Families choose the care provider they trust online first.',
    heroSub: 'Occupancy and recruitment are won digitally now. We build care websites and systems that reassure families, attract carers and keep your beds and rotas full.',
    magnet: { title: 'Free Occupancy & Recruitment Website Review', sub: "We'll assess how your website performs for both families researching care AND carers looking for work — your two hardest audiences." },
    painPoints: [
      { title: 'Families research before they ever call', text: "Choosing care is emotional. If your website doesn't radiate warmth, professionalism and CQC confidence, families quietly move to the next provider." },
      { title: 'The carer shortage is a funnel problem', text: 'Great carers are applying somewhere — just not to a clunky PDF application. We build recruitment funnels that make applying effortless.' },
      { title: 'Enquiries arrive out of hours', text: 'Care decisions happen at 10pm after a difficult day. AI assistance answers questions and captures details with empathy — whenever families need it.' },
      { title: 'Compliance eats your week', text: 'Automated enquiry logging, follow-ups and document chasing reduce the admin burden on your registered manager.' },
    ],
    build: ['A warm, trust-building website for families', 'Carer recruitment funnel with simple mobile application', 'AI assistant answering family questions 24/7', 'Automated enquiry follow-up for occupancy leads', 'Review and testimonial system (carehome.co.uk & Google)', 'Local SEO so you appear when families search "care near me"'],
    stats: [
      { value: 2, suffix: 'x', label: 'more carer applications' },
      { value: 35, suffix: '%', label: 'more occupancy enquiries' },
      { value: 24, suffix: '/7', label: 'family questions answered' },
    ],
    testimonial: {
      quote: 'Our occupancy enquiries doubled within a quarter, and the recruitment funnel finally brought us carers who actually turn up to interview. The whole system just works quietly in the background.',
      name: 'Helen Marsh',
      role: 'Willowbrook Care Group',
    },
  },
  {
    slug: 'recruitment',
    name: 'Recruitment',
    label: 'Recruitment Agencies',
    icon: 'Users',
    color: 'bg-blue-600',
    offerLine: 'Attract more candidates and win more clients — for less than £14 per day. One billable placement covers months of cost.',
    short: 'Recruitment & staffing agencies of every specialism.',
    heroHeadline: 'Your consultants should be placing people. Not chasing them.',
    heroSub: 'Candidate attraction, screening and client nurture — automated. We build recruitment systems that fill your pipeline while your team focuses on the conversations that bill.',
    magnet: { title: 'Free Candidate Attraction Audit', sub: "We'll review your website, job pages and application flow — and show you where strong candidates drop out before you ever see them." },
    painPoints: [
      { title: 'Candidates drop out of clunky applications', text: 'Every extra form field loses applicants. We build friction-free, mobile-first application flows that convert browsers into candidates.' },
      { title: 'Your website looks like every other agency', text: "Clients judge your agency the way you judge a CV — in seconds. A premium digital presence wins better roles at better margins." },
      { title: 'Screening swallows whole days', text: 'AI pre-screening qualifies candidates against your criteria before a consultant picks up the phone.' },
      { title: 'Past candidates go cold', text: 'Your database is an asset most agencies waste. Automated nurture keeps candidates warm for the next role.' },
    ],
    build: ['A premium agency website that wins clients and candidates', 'Friction-free mobile application funnels', 'AI candidate pre-screening and qualification', 'Automated candidate nurture sequences', 'Client lead generation and follow-up automation', 'Conversion tracking across every role and source'],
    stats: [
      { value: 3, suffix: 'x', label: 'more completed applications' },
      { value: 70, suffix: '%', label: 'less time spent screening' },
      { value: 40, suffix: '%', label: 'more client enquiries' },
    ],
    testimonial: {
      quote: 'I expected a website. What I got was a system. BrightLabs built our entire platform — recruitment site, candidate tools, even a free learning portal for care professionals. It positions us miles ahead of generalist agencies.',
      name: 'Brightridge',
      role: 'Specialist Care Recruitment — UK',
      link: 'https://www.brightridge.uk',
    },
  },
  {
    slug: 'gyms',
    name: 'Gyms',
    label: 'Gyms & Fitness',
    icon: 'Dumbbell',
    color: 'bg-violet-600',
    offerLine: 'Generate more membership enquiries and look more professional online — for less than £14 per day. A handful of new members covers it entirely.',
    short: 'Gyms, studios, PTs and fitness businesses.',
    heroHeadline: 'Memberships are won in the follow-up. Most gyms never send one.',
    heroSub: 'Trial enquiries, ghosted leads, lapsed members — every one is recoverable with the right system. We build growth engines that fill classes and keep memberships climbing.',
    magnet: { title: 'Free Membership Growth Audit', sub: "We'll review your website, trial offer and follow-up process — and show you how many memberships you're leaving on the table each month." },
    painPoints: [
      { title: 'Trial enquiries ghost you', text: "Someone enquires Monday, you reply Wednesday, they joined elsewhere Tuesday. Instant automated responses keep you first in the race." },
      { title: 'January members vanish by March', text: 'Win-back automation re-engages lapsed members before they cancel — at a fraction of the cost of acquiring new ones.' },
      { title: 'Your front desk is a bottleneck', text: 'Class bookings, membership questions, opening hours — your AI assistant handles them all while your team coaches.' },
      { title: 'Social media likes don\'t pay rent', text: 'We connect your content to a real funnel: traffic → trial → member — with tracking that shows what actually converts.' },
    ],
    build: ['A high-energy website built to sell memberships', 'Instant trial enquiry response and booking', 'Automated lead follow-up and win-back campaigns', 'AI assistant for class and membership questions', 'Review engine that builds local reputation', 'Conversion tracking from ad to membership'],
    stats: [
      { value: 2, suffix: 'x', label: 'membership enquiries' },
      { value: 47, suffix: '%', label: 'of ghosted leads recovered' },
      { value: 31, suffix: '%', label: 'higher trial-to-member rate' },
    ],
    testimonial: {
      quote: 'Class schedules, memberships, trial bookings — the site handles it all. We look like the biggest academy in the city, and the sign-ups show it.',
      name: 'Cobra Thai',
      role: 'MMA & Martial Arts Academy',
      link: 'https://www.cobra-thai.com',
    },
  },
  {
    slug: 'clinics',
    name: 'Clinics',
    label: 'Clinics & Healthcare',
    icon: 'Stethoscope',
    color: 'bg-emerald-600',
    offerLine: 'Look trustworthy, book more patients, fill your diary — for less than £14 per day. One private patient covers months of cost.',
    short: 'Dental, physio, aesthetics, private GP & healthcare clinics.',
    heroHeadline: 'Patients judge your clinic before they ever sit in your chair.',
    heroSub: 'A premium digital presence, instant responses and effortless booking — that\'s what fills appointment books now. We build patient acquisition systems for modern clinics.',
    magnet: { title: 'Free Patient Acquisition Audit', sub: "We'll review your website, booking flow and Google presence — and show you exactly where prospective patients are dropping off." },
    painPoints: [
      { title: 'New patients shop around', text: 'They compare three clinics in ten minutes. The one with the most professional presence and easiest booking wins — every time.' },
      { title: 'Reception can\'t catch every call', text: 'Missed calls are missed bookings. AI handles enquiries, FAQs and booking requests around the clock.' },
      { title: 'No-shows cost thousands', text: 'Automated reminders and confirmations slash no-show rates without your team lifting a finger.' },
      { title: 'Five-star care, three reviews', text: 'Your patients love you — but nobody asks them to say so. Automated review requests turn great care into great rankings.' },
    ],
    build: ['A premium clinic website that builds instant trust', 'Online booking integration and automation', 'AI assistant for patient questions 24/7', 'Automated appointment reminders to cut no-shows', 'Review generation for Google and healthcare platforms', 'Local SEO so you rank for treatments in your area'],
    stats: [
      { value: 45, suffix: '%', label: 'more new patient bookings' },
      { value: 60, suffix: '%', label: 'fewer no-shows' },
      { value: 5, suffix: 'x', label: 'more Google reviews' },
    ],
    testimonial: {
      quote: 'The booking automation and reminders transformed our diary — no-shows are down by more than half, and new patient numbers keep climbing month on month.',
      name: 'Dr. Priya Sharma',
      role: 'Brookside Dental & Aesthetics',
    },
  },
  {
    slug: 'local-business',
    name: 'Local Business',
    label: 'Local & Hospitality',
    icon: 'Store',
    color: 'bg-orange-500',
    offerLine: 'Compete with the chains — for less than £7 per day. Less than the cost of a takeaway, and a fraction of one good weekend\'s extra trade.',
    short: 'Cafés, restaurants, retail & independent local businesses.',
    heroHeadline: 'Local independents deserve big-brand presence.',
    heroSub: 'Your product is better than the chains — your online presence should be too. We build websites and systems that turn local independents into local institutions.',
    magnet: { title: 'Free Website & Lead Generation Audit', sub: "We'll review your website, Google presence and online ordering or enquiry flow — and show you where customers are slipping away." },
    painPoints: [
      { title: 'Delivery apps eat your margin', text: 'Third-party apps take up to 30%. Direct ordering through your own site keeps the margin — and the customer — yours.' },
      { title: 'Invisible on Google Maps', text: 'Most local searches end in a visit within 24 hours. If you\'re not in the map pack, you\'re not in the running.' },
      { title: 'Regulars you never hear from again', text: 'Email and SMS automation brings customers back — offers, news and reminders that feel personal, sent automatically.' },
      { title: 'No time for marketing', text: 'You\'re running the floor, the stock, the team. Your growth system runs itself in the background.' },
    ],
    build: ['A stunning website that captures your brand', 'Direct online ordering or booking — skip the app fees', 'Google Business Profile that dominates local search', 'Automated review collection from happy customers', 'Email & SMS campaigns that bring customers back', 'WhatsApp integration for instant customer contact'],
    stats: [
      { value: 30, suffix: '%', label: 'margin saved vs delivery apps' },
      { value: 2.5, suffix: 'x', decimals: 1, label: 'more direct orders' },
      { value: 4.9, suffix: '★', decimals: 1, label: 'average client rating' },
    ],
    testimonial: {
      quote: 'BrightLabs built us a website that genuinely makes people hungry \u2014 and a custom CRM that keeps every order, delivery and wholesale enquiry in one place. It works as hard as we do, and it looks incredible.',
      name: 'SugaSpice Bakery & Kitchen',
      role: 'St Neots, Cambridgeshire',
      link: 'https://www.sugaspice.uk',
    },
  },
  {
    slug: 'professional-services',
    name: 'Professional',
    label: 'Professional Services',
    icon: 'Briefcase',
    color: 'bg-zinc-700',
    offerLine: 'Win higher-value clients with a premium online presence — for less than £14 per day. One new retainer pays for the year.',
    short: 'Accountants, solicitors, consultants & financial services.',
    heroHeadline: 'Expertise wins the work. Presence wins the enquiry.',
    heroSub: 'Clients buy confidence before they buy advice. We build authoritative digital presences and intake systems for firms that should be charging more.',
    magnet: { title: 'Free Website & Client Acquisition Audit', sub: "We'll review your website, positioning and enquiry process — and show you why prospects choose competitors with weaker expertise but stronger presence." },
    painPoints: [
      { title: 'Referrals only go so far', text: 'A referral still Googles you before calling. A dated website undermines the recommendation before you ever speak.' },
      { title: 'Enquiries arrive — then sit', text: 'Professional buyers expect responsiveness. Automated acknowledgement and intelligent routing means no enquiry waits.' },
      { title: 'You compete on fees, not value', text: 'Premium positioning lets you charge for expertise. We build presence that justifies your rates.' },
      { title: 'Onboarding paperwork drags', text: 'Automated intake, document collection and engagement letters cut days off onboarding.' },
    ],
    build: ['An authoritative website that justifies premium fees', 'Intelligent enquiry capture and routing', 'Automated client onboarding and document collection', 'Thought-leadership content that builds authority', 'Review and testimonial systems for trust signals', 'Email nurture for long-cycle prospects'],
    stats: [
      { value: 40, suffix: '%', label: 'more qualified enquiries' },
      { value: 3, suffix: ' days', label: 'faster client onboarding' },
      { value: 25, suffix: '%', label: 'higher average fees' },
    ],
    testimonial: {
      quote: 'We came for a website refresh and left with an entire client intake system. New enquiries are acknowledged instantly, routed properly, and our onboarding is days faster.',
      name: 'James Whitfield',
      role: 'Whitfield & Co Accountants',
    },
  },
];

export const getIndustry = (slug) => INDUSTRIES.find((i) => i.slug === slug);
